import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import omit from 'lodash/omit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EducationFormValidationSchemaFieldNames } from './schema';
import { useToast } from '@medely/ui-kit';

export const useEducationFormMutations = () => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate: addEducation, isLoading: isAddingEducation } = useMutation(
    ({
      values,
      assignment_request_id,
    }: {
      values: EducationFormValidationSchemaFieldNames;
      assignment_request_id?: number | null;
    }) =>
      axios.post('/v3/professional/educations', {
        assignment_request_id,
        education: values,
      }),
    {
      onError: () => {
        toast.error('We had an issue, please try again.');
      },
      onSuccess: () => {
        toast.success('Education added!');
        queryClient.invalidateQueries(['GetEducationsForResumeBuilder']);
      },
    },
  );

  const { mutate: editEducation, isLoading: isEditingEducation } = useMutation(
    ({
      values,
      assignment_request_id,
    }: {
      values: EducationFormValidationSchemaFieldNames & { id?: number };
      assignment_request_id?: number | null;
    }) =>
      axios.put(`/v3/professional/educations/${values.id}`, {
        assignment_request_id,
        education: omit(values, ['id', 'start_date']),
      }),
    {
      onError: () => {
        toast.error('We had an issue, please try again.');
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['GetEducationsForResumeBuilder']);
        toast.success('Education updated!');
      },
    },
  );

  return {
    addEducation,
    editEducation,
    isLoading: isAddingEducation || isEditingEducation,
  };
};
